import { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import PropTypes from 'prop-types';
import 'react-phone-input-2/lib/style.css'; // Make sure this import is present

/**
 * An inline-edit field for phone numbers using react-phone-input-2.
 * - Display mode: shows phoneNumber as text
 * - Edit mode: a PhoneInput + "Save" / "Cancel" buttons
 * - Pressing Enter or clicking "Save" calls onSubmit({ phoneNumber: newValue })
 * - Clicking "Cancel" or pressing ESC reverts to the old value
 */
export const EditablePhoneField = ({ label, name, value, onSubmit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [phoneValue, setPhoneValue] = useState(value || '');

  const beginEdit = () => {
    setIsEditing(true);
    setPhoneValue(value || '');
  };

  const handleSave = async () => {
    // Only call onSubmit if the value actually changed
    if (phoneValue !== value) {
      await onSubmit({ [name]: phoneValue });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setPhoneValue(value || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <div className="py-2 grid grid-cols-2 gap-4">
      {/* LABEL */}
      <div className="text-sm font-light text-gray-500 dark:text-gray-400">
        {label}
      </div>

      {/* VALUE OR EDIT FIELD */}
      <div className="text-sm font-bold text-gray-900 dark:text-gray-200">
        {isEditing ? (
          <div className="flex flex-col space-y-2">
            <PhoneInput
              country="us"
              value={phoneValue}
              onChange={(val) => setPhoneValue(val)}
              onKeyDown={handleKeyDown}
              inputStyle={{
                width: '100%',
                fontSize: '0.875rem',
                padding: '0.45rem 2.4rem 0.45rem 2.4rem',
              }}
              buttonStyle={{
                padding: '0.45rem 0.6rem',
              }}
            />
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={handleSave}
                className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-2 py-1 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <span
            className="cursor-pointer"
            onClick={beginEdit}
          >
            {value || <em className="font-light text-gray-400">No phone number</em>}
          </span>
        )}
      </div>
    </div>
  );
};

EditablePhoneField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired, // e.g., 'phoneNumber'
  value: PropTypes.string,
  onSubmit: PropTypes.func.isRequired, // function({ phoneNumber: '+1234567890' })
};
